import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import * as React from 'react';
import { FC, useLayoutEffect, useState } from 'react';
import { Responsive } from 'src/@context/Responsive';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { divisorColor } from 'src/constants/stylesheet';
import ViewMonitor from 'src/utils/ViewMonitor';
import Panels from './Panels';

const useStyles = makeStyles(() =>
  createStyles({
    section: {
      position: 'relative',
      display: 'flex',
      flexWrap: 'wrap',

      [mediaQueries.Mobile]: {
        paddingLeft: `${spacing.Gutter.Mobile - spacing.GutterGrid.Mobile / 2}px`,
        paddingRight: `${spacing.Gutter.Mobile - spacing.GutterGrid.Mobile / 2}px`,

        paddingTop: spacing.Gutter.Mobile * 0.75,
      },
      [mediaQueries.Tablet]: {
        paddingLeft: `${spacing.Gutter.Tablet - spacing.GutterGrid.Tablet / 2}px`,
        paddingRight: `${spacing.Gutter.Tablet - spacing.GutterGrid.Tablet / 2}px`,

        paddingTop: spacing.Gutter.Tablet * 0.75,
      },
      [mediaQueries.Laptop]: {
        paddingLeft: `${spacing.Gutter.Laptop - spacing.GutterGrid.Laptop / 2}px`,
        paddingRight: `${spacing.Gutter.Laptop - spacing.GutterGrid.Laptop / 2}px`,

        paddingTop: spacing.Gutter.Laptop * 0.75,
      },
      [mediaQueries.Desktop]: {
        paddingLeft: `${spacing.Gutter.Desktop - spacing.GutterGrid.Desktop / 2}px`,
        paddingRight: `${spacing.Gutter.Desktop - spacing.GutterGrid.Desktop / 2}px`,

        paddingTop: spacing.Gutter.Desktop * 0.75,
      },
      [mediaQueries.BigDesktop]: {
        paddingLeft: `${spacing.Gutter.BigDesktop - spacing.GutterGrid.BigDesktop / 2}px`,
        paddingRight: `${spacing.Gutter.BigDesktop - spacing.GutterGrid.BigDesktop / 2}px`,

        paddingTop: spacing.Gutter.BigDesktop * 0.75,
      },
    },
    divisor: {
      position: 'absolute',
      bottom: 0,
      left: '0',
      right: '0',
      padding: 0,
      margin: 0,
      height: 1,
      backgroundColor: divisorColor,
      border: 'none'
    },
    titleContainer: {
      [mediaQueries.Mobile]: {
        width: `100%`,
        paddingLeft: spacing.GutterGrid.Mobile / 2,
        paddingRight: spacing.GutterGrid.Mobile / 2,

        marginBottom: spacing.Gutter.Mobile
      },
      [mediaQueries.Tablet]: {
        width: `${1 / 3 * 100}%`,
        paddingLeft: spacing.GutterGrid.Tablet / 2,
        paddingRight: spacing.GutterGrid.Tablet / 2,

        marginBottom: spacing.Gutter.Tablet
      },
      [mediaQueries.Laptop]: {
        width: `${1 / 3 * 100}%`,
        paddingLeft: spacing.GutterGrid.Laptop / 2,
        paddingRight: spacing.GutterGrid.Laptop / 2,

        marginBottom: spacing.Gutter.Laptop
      },
      [mediaQueries.Desktop]: {
        width: `${1 / 3 * 100}%`,
        paddingLeft: spacing.GutterGrid.Desktop / 2,
        paddingRight: spacing.GutterGrid.Desktop / 2,

        marginBottom: spacing.Gutter.Desktop
      },
      [mediaQueries.BigDesktop]: {
        width: `${1 / 4 * 100}%`,
        paddingLeft: spacing.GutterGrid.BigDesktop / 2,
        paddingRight: spacing.GutterGrid.BigDesktop / 2,

        marginBottom: spacing.Gutter.BigDesktop
      },
    },
    stickyContainer: {
      position: 'sticky',
      marginTop: 'auto',

      [mediaQueries.Mobile]: {
        bottom: spacing.Gutter.Mobile,
      },
      [mediaQueries.Tablet]: {
        bottom: spacing.Gutter.Tablet,
      },
      [mediaQueries.Laptop]: {
        bottom: spacing.Gutter.Laptop,
      },
      [mediaQueries.Desktop]: {
        bottom: spacing.Gutter.Desktop,
      },
      [mediaQueries.BigDesktop]: {
        bottom: spacing.Gutter.BigDesktop,
      },
    },
    title: {
      ...stylesheet.SubTitle
    },
    hideDivisor: {
      opacity: 0,
      transform: 'translate(0%, 20px)',
      transition: '.2s all linear',
    },
    showDivisor: {
      opacity: 1,
      transform: 'translate(0%, 0px)',
    },
    hide: {
      ...stylesheet.Hide
    },
    show: {
      ...stylesheet.Show
    },
  }));

export type sectionProps = SectionDto;

type Props = sectionProps;

export const getWindowSize = () => {
  const [size, setSize] = useState([0, 0]);

  useLayoutEffect(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

const Section: FC<Props> = (props) => {
  const cls = useStyles();
  const { children, Title, Panels: panels, UID } = props;

  const readMorePanelIndex = panels.findIndex(p => p.type === "PanelReadMore");
  const readMorePanel = readMorePanelIndex > 0 ? panels[readMorePanelIndex] as PanelReadMoreDto : null;
  const visiblePanels = readMorePanelIndex > 0 ? panels.slice(0, readMorePanelIndex) : panels;
  const hiddenPanels = readMorePanelIndex > 0 ? panels.slice(readMorePanelIndex + 1) : undefined;

  const readMore =
    hiddenPanels &&
      hiddenPanels.length > 0 &&
      readMorePanel ?
      <span>
        {readMorePanel.ReadMore}
      </span> : undefined;

  return (
    <div className={cls.section} id={UID}>
      <ViewMonitor once={true}>{(intersect) =>
        <div className={
          classNames(
            cls.titleContainer,
            cls.hide,
            children && cls.stickyContainer,
            intersect && cls.show)}
        >
          {children &&
            <>
              <Responsive steps={['AfterMobile']}>
                <nav>
                  {children}
                </nav>
              </Responsive>
              <Responsive steps={['Mobile']}>
                <h5 className={cls.title}>{Title}</h5>
              </Responsive>
            </>
          }
          {!children &&
            <h5 className={cls.title}>{Title}</h5>
          }
        </div>}
      </ViewMonitor>
      <Panels visiblePanels={visiblePanels} hiddenPanels={hiddenPanels} panelReadMore={readMore} />
      <ViewMonitor once={true}>{(intersect) =>
        <hr className={classNames(cls.divisor, cls.hideDivisor, intersect && cls.showDivisor)} />
      }</ViewMonitor>
    </div>
  );
};

export default Section;


// WEBPACK FOOTER //
// ./src/Components/Section.tsx